import React, { useState, useEffect } from "react";
import SideDrawer from "../components/SideDrawer";
import Header from "../components/Header";
import InputForm from "../components/InputForm";
import { MDBContainer, MDBBtn, MDBCard, MDBCardBody } from "mdb-react-ui-kit";
import { Toast, Spinner } from "react-bootstrap";
import { useAuth } from "../contexts/AuthContext";
import { callOpenAIAPI } from "../services/api";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import '../styles/CustomGPT.css';
import CopySvg from '../assets/icons/copy-icon.svg';
import SaveSvg from '../assets/icons/save-icon.svg';
import { motion } from "framer-motion";
import MainLayout from "./MainLayout";

const buildPrompt = (formData) => `
You are a campaign strategist creating a high-converting influencer brief.

Based on the following client and campaign information, generate a full campaign brief using the Social Jet 7-Step Viral Selling System. Include key strategy points such as the target audience profile, core messaging, influencer content format, promotional offer, price positioning, CTA, and distribution strategy.
Important rules:
-Be bold and professional
-Copywriting tome: Be Emotional, persuasive, scroll-stopping
-Hooks: Specific, benefit-driven, outcome-based
-Offer Clarity: Clear and specific promo with researched statistics and details
-CTA Copy: Native + urgent

-------------------------
🔖 CAMPAIGN / CLIENT INFO:
- Company Name: ${formData.companyName || "Not provided"}
- Product/Service: ${formData.productName || "Not provided"}
- Website: ${formData.website || "Not provided"}
- Instagram: ${formData.instagram || "Not provided"}
- TikTok: ${formData.tiktok || "Not provided"}
- Facebook: ${formData.facebook || "Not provided"}
- Description: ${formData.description || "Not provided"}
- Campaign Duration: ${formData.duration || "Not provided"}
- Number of Influencers: ${formData.influencers || "Not provided"}
- Number of Videos: ${formData.videos || "Not provided"}

🎯 CAMPAIGN OBJECTIVES:
- Primary Objectives: ${formData.objectives?.join(", ") || "Not provided"}
- Custom Objective: ${formData.customObjective || "None"}

👥 TARGET AUDIENCE:
- Age Group: ${formData.ageGroup || "Not provided"}
- Gender: ${formData.genderPreference?.join(", ") || "Not provided"}
- Additional Details: ${formData.audienceDetails || "Not provided"}

💬 MARKETING MESSAGE:
- Summary: ${formData.marketingSummary || "Not provided"}
- Main Focus: ${formData.mainFocus || "Not provided"}
- Platform Focus: ${formData.platformFocus?.join(", ") || "Not provided"}

🛍️ OFFER / PROMOTION:
- ${formData.offerPromotion || "Not provided"}

💰 PRICE POINT:
- ${formData.pricePoint || "Not provided"}

📣 CALL TO ACTION (CTA):
- Destination: ${formData.ctaDestination || "Not provided"}
- Action: ${formData.ctaAction || "Not provided"}

📝 CONTENT REQUIREMENTS / SPECIAL NOTES:
- Must-show Features/Visuals: ${formData.specialNotes || "None"}
- Tone of the Video: ${formData.tone || "Not provided"}
- Additional Notes: ${formData.additionalNotes || "None"}

-------------------------

Use the Social Jet 7-Step Viral Selling System to structure this campaign strategy clearly and persuasively.
I Would like you to suggest me influencer shortlists, caption variations, or video hooks.
`;

function CustomGPT() {
  const { isCollapsed } = useAuth();
  const [formData, setFormData] = useState({
    companyName: "",
    productName: "",
    website: "",
    instagram: "",
    tiktok: "",
    facebook: "",
    description: "",
    duration: "",
    influencers: "",
    videos: "",
    objectives: [],
    customObjective: "",
    ageGroup: "",
    genderPreference: [],
    audienceDetails: "",
    ctaDestination: "",
    ctaAction: "",
    marketingSummary: "",
    mainFocus: "",
    platformFocus: [],
    offerPromotion: "",
    pricePoint: "",
    specialNotes: "",
    tone: "",
    additionalNotes: "",
  });

  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('green');
  const [showToast, setShowToast] = useState(false);
  const [responseContent, setResponseContent] = useState("No response yet...");
  const [isLoading, setIsLoading] = useState(false);
  const [hoveredBtn, setHoveredBtn] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData) return;
    setIsLoading(true);
    const prompt = buildPrompt(formData);

    try {
      const response = await callOpenAIAPI({ prompt });
      const content = response.data.choices?.[0]?.message?.content;
      setResponseContent(content || "No response content received.");
      setIsLoading(false);
    } catch (error) {
      setResponseContent(`Error: ${error.message}`);
      setIsLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(responseContent);
      setToastMessage('Content copied to clipboard!');
      setToastType('green');
      setShowToast(true);
    } catch (err) {
      setToastMessage(`Failed to copy content: ${err.message}`);
      setToastType('red');
      setShowToast(true);
    }
  };

  const handleSave = () => {
    const blob = new Blob([responseContent], { type: "text/plain" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "response.txt";
    link.click();
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      const container = document.querySelector(".custom-resizable-container");
      const leftPane = document.querySelector(".left-pane");
      const offsetLeft = container.offsetLeft;
      const pointerX = e.clientX - offsetLeft;
      const min = 250;
      const max = container.clientWidth - 250;
      if (pointerX > min && pointerX < max) {
        leftPane.style.flex = `0 0 ${pointerX}px`;
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    const handleMouseDown = () => {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    };

    const resizer = document.querySelector(".resizer");
    if (resizer) resizer.addEventListener("mousedown", handleMouseDown);
    return () => {
      if (resizer) resizer.removeEventListener("mousedown", handleMouseDown);
    };
  }, []);

  return (
    <MainLayout>
      <MDBContainer className="px-0 m-0 activity-log-container">
        <div className="custom-resizable-container">
          <div className="left-pane bg-transparent px-1">
            <InputForm
              formData={formData}
              setFormData={setFormData}
              handleSubmit={handleSubmit}
            />
          </div>

          <div className="resizer" />

          <div className="right-pane">
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
              <MDBCard className="p-3 shadow-5 rounded-4" style={{ minHeight: "300px" }}>
                <MDBCardBody>
                  {isLoading ? (
                    <div className="d-flex align-items-center justify-content-center gap-2 py-4">
                      <Spinner animation="border" role="status" />
                      <span className="fw-semibold">Loading...</span>
                    </div>
                  ) : (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }} className="markdown-content">
                      <div className="d-flex gap-3 flex-wrap justify-content-between border-bottom mb-4">
                        <h3>Generated Brief</h3>
                        <div className="d-flex gap-3 mb-2 flex-wrap justify-content-end">
                          <motion.div whileHover={{ scale: 1.05 }} onMouseEnter={() => setHoveredBtn("copy")} onMouseLeave={() => setHoveredBtn(null)}>
                            <MDBBtn color="info" className="shadow-3 d-flex align-items-center" style={{ backgroundColor: "#a881af" }} onClick={handleCopy}>
                              <img src={CopySvg} width={18} height={18} alt="copy" className="me-1" />
                              <motion.span initial={{ opacity: 0, x: 5 }} animate={{ opacity: hoveredBtn === "copy" ? 1 : 0, x: hoveredBtn === "copy" ? 0 : 5, width: hoveredBtn === "copy" ? 'auto' : '0px' }} transition={{ duration: 0.3 }} style={{ overflow: "hidden", whiteSpace: "nowrap" }}>
                                Copy
                              </motion.span>
                            </MDBBtn>
                          </motion.div>
                          <motion.div whileHover={{ scale: 1.05 }} onMouseEnter={() => setHoveredBtn("save")} onMouseLeave={() => setHoveredBtn(null)}>
                            <MDBBtn color="success" className="shadow-3 d-flex align-items-center" style={{ backgroundColor: "#80669d" }} onClick={handleSave}>
                              <img src={SaveSvg} width={14} height={14} alt="save" className="me-1" />
                              <motion.span initial={{ opacity: 0, x: 5 }} animate={{ opacity: hoveredBtn === "save" ? 1 : 0, x: hoveredBtn === "save" ? 0 : 5, width: hoveredBtn === "save" ? 'auto' : '0px' }} transition={{ duration: 0.3 }} style={{ overflow: "hidden", whiteSpace: "nowrap" }}>
                                Save
                              </motion.span>
                            </MDBBtn>
                          </motion.div>
                        </div>
                      </div>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{responseContent}</ReactMarkdown>
                    </motion.div>
                  )}
                </MDBCardBody>
              </MDBCard>
            </motion.div>
          </div>
        </div>
        <Toast show={showToast} onClose={() => setShowToast(false)} position="top-right" style={{ position: 'absolute', top: 0, right: 0, zIndex: 2000, backgroundColor: toastType }} delay={3000} autohide>
          <Toast.Body className='text-white'>{toastMessage}</Toast.Body>
        </Toast>
      </MDBContainer>
    </MainLayout>
  );
}

export default CustomGPT;